import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/Button.js';
import './reset-demo-dialog.css';

/** The demonstration project, as the confirmation asks for it. */
const PROJECT_NAME = 'Emploi public';

interface ResetDemoDialogProps {
  busy: boolean;
  error: string | null;
  onConfirm: (confirmation: string) => void;
  onCancel: () => void;
}

/**
 * Back to zero before a rehearsal or a training (ADR 0013). Says what goes and
 * what stays, and asks for the project's name so a stray click erases nothing.
 */
export function ResetDemoDialog({ busy, error, onConfirm, onCancel }: ResetDemoDialogProps) {
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim().toLocaleLowerCase('fr') === PROJECT_NAME.toLocaleLowerCase('fr');

  // Rendered on the body: the top bar would otherwise be the frame of a fixed overlay.
  return createPortal(
    <div className="reset-demo" role="dialog" aria-modal="true" aria-labelledby="reset-demo-title">
      <div className="reset-demo__panel">
        <h3 id="reset-demo-title" className="reset-demo__title">
          Remettre le projet à zéro
        </h3>
        <div className="reset-demo__lists">
          <div>
            <p className="reset-demo__heading">Effacé</p>
            <ul>
              <li>les sessions mesurées et les avis recueillis</li>
              <li>les parcours, les analyses et les comparaisons avant / après</li>
              <li>les recommandations, les aides et les versions mises en ligne</li>
            </ul>
          </div>
          <div>
            <p className="reset-demo__heading">Gardé</p>
            <ul>
              <li>le projet et sa balise d'intégration</li>
              <li>les comptes et le journal des actions</li>
            </ul>
          </div>
        </div>
        <p className="reset-demo__hint">
          Le tableau de bord sera vide. Rechargez ensuite « Corpus de référence » depuis ce menu, ou laissez
          arriver les visites du portail.
        </p>
        <label className="reset-demo__field">
          <span>
            Pour confirmer, tapez <strong>{PROJECT_NAME}</strong>
          </span>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            aria-label={`Tapez ${PROJECT_NAME} pour confirmer`}
          />
        </label>
        {error && (
          <p className="reset-demo__error" role="alert">
            {error}
          </p>
        )}
        <div className="reset-demo__actions">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Annuler
          </Button>
          <Button variant="danger" disabled={!confirmed || busy} onClick={() => onConfirm(typed)}>
            {busy ? 'Remise à zéro…' : 'Tout remettre à zéro'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
